import { useEffect, useState } from 'react';
import type { LifeAreaTag } from '@contracts';
import { LifeAreaTagSelect } from './LifeAreaTagSelect';
import { todayInputStyle, TodayCard } from './primitives';

const REFLECTION_MAX_LENGTH = 140;

/**
 * Row D (420px) — Favorite thing about today (R8.3, design/screens/
 * today-desktop-v2.md SS10.2). 0-5 `LifeAreaTag` selections + a bounded
 * free-text reflection with an always-visible counter (AC-T9: never a silent
 * failure — input is truncated at the bound).
 */
export function FavoriteThingCard({
  favoriteMoment,
  onSave,
}: {
  readonly favoriteMoment: { readonly tags: readonly LifeAreaTag[]; readonly reflection: string } | null;
  readonly onSave: (input: { readonly tags: readonly LifeAreaTag[]; readonly reflection: string }) => void;
}) {
  const [tags, setTags] = useState<readonly LifeAreaTag[]>(favoriteMoment?.tags ?? []);
  const [reflection, setReflection] = useState(favoriteMoment?.reflection ?? '');
  // Editing is keyed off whether the REFLECTION has content, not whether a
  // `favoriteMoment` event exists — a tag-only save (empty reflection) still
  // leaves the textarea open (SS10.2: "while unsaved/empty, no Edit action
  // renders"), so toggling a tag must never silently collapse the open
  // textarea out from under a mid-edit user.
  const [editing, setEditing] = useState((favoriteMoment?.reflection ?? '').trim().length === 0);

  // Bug fix (qa-engineer sweep, reproduced live against the phone twin of
  // this card, same root cause here): keying the resync on
  // `favoriteMoment?.reflection` ALONE left `tags` stale whenever two dates'
  // reflection text happened to match (most commonly both '' -- the common
  // case for a tags-only save) -- day-paging between two such dates would
  // silently carry the previous date's tags into the newly-viewed one.
  // Both fields now gate the resync, compared BY VALUE (`tagsKey`), not by
  // `favoriteMoment` object/array identity, which is a fresh reference on
  // every `getDailyPage` reload even for the SAME date and would otherwise
  // wipe in-progress typing on every unrelated sibling-card save -- `editing`
  // still derives from the reflection text specifically (the one field that
  // decides editing/read-only), preserving this effect's original intent.
  const tagsKey = (favoriteMoment?.tags ?? []).join(',');
  const reflectionKey = favoriteMoment?.reflection ?? '';
  useEffect(() => {
    setTags(favoriteMoment?.tags ?? []);
    setReflection(favoriteMoment?.reflection ?? '');
    setEditing((favoriteMoment?.reflection ?? '').trim().length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsKey, reflectionKey]);

  function toggleTag(tag: LifeAreaTag) {
    const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag].slice(0, 5);
    setTags(next);
    onSave({ tags: next, reflection });
  }

  function commitReflection() {
    onSave({ tags, reflection });
    if (reflection.trim().length > 0) setEditing(false);
  }

  return (
    <TodayCard label="My favorite thing about today was..." style={{ width: '420px', flex: 'none' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
        My favorite thing about today was...
      </h2>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <LifeAreaTagSelect value={tags} onToggle={toggleTag} />
      </div>

      {editing ? (
        <>
          <textarea
            aria-label="Favorite-thing reflection"
            value={reflection}
            onChange={(e) => setReflection(e.target.value.slice(0, REFLECTION_MAX_LENGTH))}
            onBlur={commitReflection}
            placeholder="One sentence is plenty."
            rows={2}
            style={{ ...todayInputStyle, marginTop: 'var(--space-3)', minHeight: '56px', padding: 'var(--space-2) var(--space-3)', resize: 'none' }}
          />
          <div style={{ marginTop: 'var(--space-1)', textAlign: 'right', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
            {reflection.length} / {REFLECTION_MAX_LENGTH}
          </div>
        </>
      ) : (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{reflection}</p>
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--hue-primary)' }}
          >
            Edit
          </button>
        </div>
      )}
    </TodayCard>
  );
}
